import { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import { API } from "@/App";
import { Navigation, MapPin, Phone, Car, Clock, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import "leaflet/dist/leaflet.css";

// XMLHttpRequest-based fetch to avoid Emergent script conflicts
const safeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    const headers = options.headers || {};
    Object.entries(headers).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });
    xhr.onload = function() {
      let data;
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; }
      catch { data = xhr.responseText; }
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        json: () => Promise.resolve(data),
        text: () => Promise.resolve(xhr.responseText)
      });
    };
    xhr.onerror = () => reject(new Error('Network error'));
    if (options.body) {
      xhr.send(typeof options.body === 'string' ? options.body : JSON.stringify(options.body));
    } else {
      xhr.send();
    }
  });
};

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom icons
const createIcon = (color, type = "default") => {
  const svgIcon = type === "car" 
    ? `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${color}" width="32" height="32"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg>`
    : `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${color}" width="28" height="28"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`;
  
  return L.divIcon({
    html: svgIcon,
    className: "custom-marker",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

const pickupIcon = createIcon("#22c55e"); // green
const dropIcon = createIcon("#ef4444"); // red
const driverIcon = createIcon("#1B4332", "car"); // forest green car

// Map center updater component
function MapUpdater({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || map.getZoom());
    }
  }, [center, zoom, map]);
  return null;
}

export default function RideTrackingMap({ 
  rideId, 
  token,
  pickupLocation,
  dropLocation,
  driverLocation,
  rideStatus,
  driverName,
  driverPhone,
  estimatedTime,
  onRefresh
}) {
  const [tracking, setTracking] = useState({
    pickup: pickupLocation || null,
    drop: dropLocation || null,
    driver: driverLocation || null,
  });
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const intervalRef = useRef(null);

  // Default center (Kashmir region)
  const defaultCenter = [34.0837, 74.7973]; // Srinagar

  useEffect(() => {
    // Update tracking when props change
    setTracking({
      pickup: pickupLocation || tracking.pickup,
      drop: dropLocation || tracking.drop,
      driver: driverLocation || tracking.driver,
    });
  }, [pickupLocation, dropLocation, driverLocation]);

  useEffect(() => {
    if (rideId && token && ["assigned", "accepted", "started"].includes(rideStatus)) {
      // Start polling for driver location
      fetchDriverLocation();
      intervalRef.current = setInterval(fetchDriverLocation, 10000); // Every 10 seconds
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [rideId, rideStatus]);

  const fetchDriverLocation = async () => {
    if (!rideId || !token) return;
    setLoading(true);
    try {
      const res = await safeFetch(`${API}/rides/${rideId}/tracking`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.driver_location) {
          setTracking(prev => ({
            ...prev,
            driver: data.driver_location
          }));
          setLastUpdate(new Date());
        }
      }
    } catch (err) {
      console.error("Failed to fetch driver location:", err);
    }
    setLoading(false);
  };

  const handleRefresh = () => {
    fetchDriverLocation();
    if (onRefresh) onRefresh();
  };

  // Calculate map center
  const getMapCenter = () => {
    if (tracking.driver) return [tracking.driver.lat, tracking.driver.lng];
    if (tracking.pickup) return [tracking.pickup.lat, tracking.pickup.lng];
    return defaultCenter;
  };

  // Create route path
  const getRoutePath = () => {
    const points = [];
    if (tracking.pickup) points.push([tracking.pickup.lat, tracking.pickup.lng]);
    if (tracking.driver) points.push([tracking.driver.lat, tracking.driver.lng]);
    if (tracking.drop) points.push([tracking.drop.lat, tracking.drop.lng]);
    return points;
  };

  const isActiveRide = ["assigned", "accepted", "started"].includes(rideStatus);

  return (
    <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm" data-testid="ride-tracking-map">
      {/* Header */}
      <div className="bg-white p-4 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-forest rounded-xl flex items-center justify-center">
              <Navigation className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Live Tracking</h3>
              <p className="text-xs text-slate-500">
                {lastUpdate ? `Updated ${formatTimeAgo(lastUpdate)}` : "Waiting for updates..."}
              </p>
            </div>
          </div>
          {isActiveRide && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
              className="rounded-full"
              data-testid="refresh-location-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          )}
        </div>

        {/* Driver Info */}
        {driverName && isActiveRide && (
          <div className="mt-3 flex items-center justify-between bg-slate-50 rounded-xl p-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-forest/10 rounded-full flex items-center justify-center">
                <Car className="w-5 h-5 text-forest" />
              </div>
              <div>
                <p className="font-medium text-slate-900">{driverName}</p>
                <p className="text-xs text-slate-500">Your driver is {rideStatus === "started" ? "on the way" : "coming to pick you up"}</p>
              </div>
            </div>
            {driverPhone && (
              <a
                href={`tel:${driverPhone}`}
                className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white hover:bg-green-600 transition-colors"
              >
                <Phone className="w-5 h-5" />
              </a>
            )}
          </div>
        )}

        {/* ETA */}
        {estimatedTime && isActiveRide && (
          <div className="mt-3 flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-amber" />
            <span className="text-slate-600">Estimated arrival:</span>
            <span className="font-medium text-slate-900">{estimatedTime}</span>
          </div>
        )}
      </div>

      {/* Map */}
      <div className="h-[300px] relative">
        <MapContainer
          center={getMapCenter()}
          zoom={13}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapUpdater center={getMapCenter()} />

          {/* Route Line */}
          {getRoutePath().length > 1 && (
            <Polyline
              positions={getRoutePath()}
              color="#1B4332"
              weight={3}
              opacity={0.7}
              dashArray="10, 10"
            />
          )}

          {/* Pickup Marker */}
          {tracking.pickup && (
            <Marker position={[tracking.pickup.lat, tracking.pickup.lng]} icon={pickupIcon}>
              <Popup>
                <div className="text-center">
                  <p className="font-medium text-green-600">Pickup</p>
                  <p className="text-sm text-slate-600">{tracking.pickup.address || "Pickup Location"}</p>
                </div>
              </Popup>
            </Marker>
          )}

          {/* Drop Marker */}
          {tracking.drop && (
            <Marker position={[tracking.drop.lat, tracking.drop.lng]} icon={dropIcon}>
              <Popup>
                <div className="text-center">
                  <p className="font-medium text-red-600">Drop-off</p>
                  <p className="text-sm text-slate-600">{tracking.drop.address || "Drop Location"}</p>
                </div>
              </Popup>
            </Marker>
          )}

          {/* Driver Marker */}
          {tracking.driver && isActiveRide && (
            <Marker position={[tracking.driver.lat, tracking.driver.lng]} icon={driverIcon}>
              <Popup>
                <div className="text-center">
                  <p className="font-medium text-forest">{driverName || "Driver"}</p>
                  <p className="text-xs text-slate-500">Last updated: {lastUpdate ? formatTimeAgo(lastUpdate) : "Just now"}</p>
                </div>
              </Popup>
            </Marker>
          )}
        </MapContainer>

        {/* Status Overlay for completed/cancelled rides */}
        {["completed", "cancelled"].includes(rideStatus) && (
          <div className="absolute inset-0 bg-slate-900/60 flex items-center justify-center">
            <div className="bg-white rounded-2xl p-6 text-center">
              <p className="text-lg font-semibold text-slate-900">
                {rideStatus === "completed" ? "Ride Completed" : "Ride Cancelled"}
              </p>
              <p className="text-sm text-slate-500 mt-1">Live tracking is no longer available</p>
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="bg-white p-3 border-t border-slate-100 flex items-center justify-center gap-6 text-xs text-slate-600">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-green-500" /> Pickup
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-red-500" /> Drop-off
        </span>
        {isActiveRide && (
          <span className="flex items-center gap-1">
            <Car className="w-4 h-4 text-forest" /> Driver
          </span>
        )}
      </div>
    </div>
  );
}

// Helper function
function formatTimeAgo(date) {
  const seconds = Math.floor((new Date() - date) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}
